import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import { faPaperPlane, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';

interface SendTestMailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTestMailSent: () => void;
}

export default function SendTestMailModal({ isOpen, onClose, onTestMailSent }: SendTestMailModalProps) {
  const [formData, setFormData] = useState({
    receiverEmail: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.receiverEmail.trim()) {
      showErrorToast('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.receiverEmail.trim())) {
      showErrorToast('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setLoading(true);

    try {
      await APIRoutes.sendTestMail(
        formData.receiverEmail.trim(),
      );
      showSuccessToast('Test-Mail wurde erfolgreich versendet');
      onTestMailSent();
      onClose();

      // Reset form
      setFormData({
        receiverEmail: '',
      });
    } catch (err) {
      handleAPIError(err, 'Fehler beim Senden der Test-Mail');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Test-Mail senden"
      icon={<FontAwesomeIcon icon={faPaperPlane} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="receiverEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Empfänger E-Mail-Adresse *
            </label>
            <input
              type="email"
              id="receiverEmail"
              name="receiverEmail"
              value={formData.receiverEmail}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="test@example.com"
              required
              disabled={loading}
            />
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Die Test-Mail wird in die Warteschlange eingereiht und automatisch versendet.
              Sie können den Status der E-Mail in der Liste verfolgen.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !formData.receiverEmail.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
                <span>Test-Mail senden</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}